/**
 * Settlement Service - 权益判断服务
 * 负责判断当前任务应该走老计次还是 Credits
 */

import type {
    SettlementContext,
    SettlementResult,
    EntitlementContext,
    PackageEntitlement,
} from '../types';
import {
    SettlementMode,
    UnavailableReason,
    BillingType,
    PackageType,
} from '../types';

export const SettlementService = Symbol('SettlementService');

export interface SettlementService {
    /**
     * 判断结算模式
     * @param context 结算上下文
     * @returns 结算结果
     */
    determineSettlementMode(context: SettlementContext): SettlementResult;

    /**
     * 根据权益上下文构建结算上下文
     * @param entitlementContext 权益上下文
     * @param featureId 功能 ID
     * @returns 结算上下文
     */
    buildSettlementContext(
        entitlementContext: EntitlementContext,
        featureId: string
    ): SettlementContext;

    /**
     * 检查功能是否有老计次权益
     * @param packages 套餐列表
     * @param featureId 功能 ID
     * @returns 老计次权益信息
     */
    checkLegacyCountEntitlement(
        packages: PackageEntitlement[],
        featureId: string
    ): { hasEntitlement: boolean; remainingCount: number };

    /**
     * 检查功能是否有 Credits 权益
     * @param packages 套餐列表
     * @param featureId 功能 ID
     * @returns 是否有 Credits 权益
     */
    checkCreditsEntitlement(
        packages: PackageEntitlement[],
        featureId: string
    ): boolean;
}

/**
 * Settlement Service 实现
 */
export class SettlementServiceImpl implements SettlementService {
    /**
     * 判断结算模式
     *
     * 规则：
     * 1. 老计次可用 → count_covered
     * 2. 老计次不可用但 Credits 可用 → credit_charge
     * 3. 都不可用 → 拦截
     */
    determineSettlementMode(context: SettlementContext): SettlementResult {
        const { hasLegacyCount, legacyCountRemaining, hasCredits, creditsAvailable } = context;

        // 优先使用老计次
        if (hasLegacyCount && legacyCountRemaining > 0) {
            return {
                mode: SettlementMode.COUNT_COVERED,
                canProceed: true,
            };
        }

        // 其次使用 Credits
        if (hasCredits && creditsAvailable > 0) {
            return {
                mode: SettlementMode.CREDIT_CHARGE,
                canProceed: true,
            };
        }

        // 有 Credits 权益但余额不足
        if (hasCredits && creditsAvailable <= 0) {
            return {
                mode: SettlementMode.CREDIT_CHARGE,
                canProceed: false,
                reason: UnavailableReason.INSUFFICIENT,
            };
        }

        // 无任何权益
        return {
            mode: SettlementMode.CREDIT_CHARGE,
            canProceed: false,
            reason: UnavailableReason.NO_ACTIVE,
        };
    }

    /**
     * 根据权益上下文构建结算上下文
     */
    buildSettlementContext(
        entitlementContext: EntitlementContext,
        featureId: string
    ): SettlementContext {
        const { packages, creditsActive } = entitlementContext;

        // 检查老计次权益
        const legacyEntitlement = this.checkLegacyCountEntitlement(packages, featureId);

        // 检查 Credits 权益
        const hasCreditsEntitlement = this.checkCreditsEntitlement(packages, featureId);

        return {
            hasLegacyCount: legacyEntitlement.hasEntitlement,
            legacyCountRemaining: legacyEntitlement.remainingCount,
            hasCredits: creditsActive && hasCreditsEntitlement,
            creditsAvailable: 0, // 需要从钱包状态获取
            featureId,
        };
    }

    /**
     * 检查功能是否有老计次权益
     */
    checkLegacyCountEntitlement(
        packages: PackageEntitlement[],
        featureId: string
    ): { hasEntitlement: boolean; remainingCount: number } {
        for (const pkg of packages) {
            // 只检查老计次类型的套餐
            if (pkg.packageType !== PackageType.LEGACY_COUNT) {
                continue;
            }

            // 检查套餐是否激活
            if (!pkg.isActive) {
                continue;
            }

            // 检查功能是否被覆盖
            if (!pkg.coveredFeatures.includes(featureId)) {
                continue;
            }

            // 检查计费类型
            if (pkg.billingType !== BillingType.COUNT) {
                continue;
            }

            return {
                hasEntitlement: true,
                remainingCount: pkg.remainingCount ?? 0,
            };
        }

        return {
            hasEntitlement: false,
            remainingCount: 0,
        };
    }

    /**
     * 检查功能是否有 Credits 权益
     */
    checkCreditsEntitlement(
        packages: PackageEntitlement[],
        featureId: string
    ): boolean {
        for (const pkg of packages) {
            // 只检查 Credits 类型的套餐
            if (pkg.packageType !== PackageType.CREDITS) {
                continue;
            }

            // 检查套餐是否激活
            if (!pkg.isActive) {
                continue;
            }

            // 检查功能是否被覆盖
            if (!pkg.coveredFeatures.includes(featureId)) {
                continue;
            }

            // 检查计费类型
            if (pkg.billingType !== BillingType.CREDITS) {
                continue;
            }

            return true;
        }

        return false;
    }
}
